/**
 * F8's exit criterion, as a screen: "APK installs, authenticates, returns a GPS fix and a
 * photo." This is that proof, not a product feature — the visit/check-in model itself is
 * M6/H3 work and does not exist yet. Once it does, this is the code the real check-in flow
 * calls; today it exists so the two risky plugins (ADR-007) have something to run on
 * hardware the day a device is available, rather than being written for the first time
 * then.
 *
 * Both plugins already have a web fallback (`navigator.geolocation`, a file input with
 * `capture`), so this page also works in a desktop browser during development — it just
 * is not proof of anything until it runs on a phone.
 */
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Geolocation, type Position } from '@capacitor/geolocation'
import { MapPin, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { openDirections } from '@/lib/maps'

type FixState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'ready'; position: Position }
  | { status: 'error'; message: string }

type PhotoState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'ready'; dataUrl: string }
  | { status: 'error'; message: string }

export function MobileCheckIn() {
  const [fix, setFix] = useState<FixState>({ status: 'idle' })
  const [photo, setPhoto] = useState<PhotoState>({ status: 'idle' })

  const captureFix = async () => {
    setFix({ status: 'pending' })
    try {
      const permission = await Geolocation.requestPermissions()
      if (permission.location === 'denied') {
        setFix({ status: 'error', message: 'Location permission was refused.' })
        return
      }
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })
      setFix({ status: 'ready', position })
    } catch (error) {
      setFix({ status: 'error', message: describe(error, 'Could not get a GPS fix.') })
    }
  }

  const capturePhoto = async () => {
    setPhoto({ status: 'pending' })
    try {
      const result = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        quality: 80,
        saveToGallery: false,
      })
      if (!result.dataUrl) throw new Error('The camera returned no image.')
      setPhoto({ status: 'ready', dataUrl: result.dataUrl })
    } catch (error) {
      setPhoto({ status: 'error', message: describe(error, 'Could not take a photo.') })
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Device check" description="GPS fix and camera, proven on this phone." />
        <CardBody className="space-y-4">
          <div className="space-y-2">
            <Button className="h-11 w-full" onClick={captureFix} loading={fix.status === 'pending'}>
              <MapPin className="h-4 w-4" aria-hidden="true" />
              Get GPS fix
            </Button>
            {fix.status === 'ready' && (
              <div className="space-y-2 rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800/60">
                <p className="tabular-nums text-slate-700 dark:text-slate-300">
                  {fix.position.coords.latitude.toFixed(6)},{' '}
                  {fix.position.coords.longitude.toFixed(6)} · ±
                  {Math.round(fix.position.coords.accuracy)}m
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-11 w-full"
                  onClick={() =>
                    openDirections({
                      latitude: fix.position.coords.latitude,
                      longitude: fix.position.coords.longitude,
                    })
                  }
                >
                  Open in Maps
                </Button>
              </div>
            )}
            {fix.status === 'error' && <Alert tone="error">{fix.message}</Alert>}
          </div>

          <div className="space-y-2">
            <Button
              variant="secondary"
              className="h-11 w-full"
              onClick={capturePhoto}
              loading={photo.status === 'pending'}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Take photo
            </Button>
            {photo.status === 'ready' && (
              <img
                src={photo.dataUrl}
                alt="Captured"
                className="w-full rounded-md border border-slate-200 dark:border-slate-800"
              />
            )}
            {photo.status === 'error' && <Alert tone="error">{photo.message}</Alert>}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

function describe(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}
